import { Fetcher } from '@this/src/util';
import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@this/components/shared/ui/feedbacks/modal';
import { Button } from '@this/components/shared/ui/inputs/button';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@this/components/shared/ui/data_displays/table';
import { Select } from '@this/components/shared/ui/inputs/select';
import { Textarea } from '@this/components/shared/ui/inputs/textarea';
import type RakutenHotelCancelInventoryItem from '@this/src/domain/arrangement/rakuten_hotel_cancel_inventory_item';
import type { RakutenHotelCancelCommentType } from '@this/src/domain/arrangement/rakuten_hotel_cancel_inventory_item';
import { rakutenHotelCancelCommentTypeLabels } from '@this/src/domain/arrangement/rakuten_hotel_cancel_inventory_item';
import Notification from '@this/src/notification';

interface Props {
  item: RakutenHotelCancelInventoryItem;
  onClose: () => void;
  onSaved: () => void;
}

const getStatusStyle = (status: RakutenHotelCancelCommentType, theme: AITTheme): React.CSSProperties => {
  switch (status) {
    case 'important':
      return { color: theme.tokens.colors.danger.primary, fontWeight: 'bold' };
    case 'warning':
      return { color: theme.tokens.colors.warning.primary, fontWeight: 'bold' };
    default:
      return {};
  }
};

const CancelInventoryCommentModal = observer(({ item, onClose, onSaved }: Props) => {
  const { theme } = useAITTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [content, setContent] = useState('');
  const [status, setStatus] = useState<RakutenHotelCancelCommentType>('normal');

  const saveComment = () => {
    const params = {
      cancel_request_id: item.id,
      content,
      status
    };
    setIsLoading(true);
    Fetcher.post('/arrangement/auto_reservation_inventory/rakuten_hotel_cancel/comments', params)
      .then(() => {
        // コメント取得APIは無く一覧レスポンスに同梱されるため、保存後は一覧を再取得して閉じる
        onSaved();
      })
      .catch(() => {
        Notification.error('コメントの保存に失敗しました');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <Modal open onClose={onClose} size="large">
      <ModalHeader>この商品に関するやり取り</ModalHeader>
      <ModalBody>
        <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '16px' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell nowrap>手配者名</TableCell>
                <TableCell nowrap>種類</TableCell>
                <TableCell>内容</TableCell>
                <TableCell nowrap>記載日時</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {item.comments.map(comment => (
                <TableRow key={comment.id}>
                  <TableCell nowrap>{comment.commentedByName}</TableCell>
                  <TableCell nowrap style={getStatusStyle(comment.status, theme)}>
                    {rakutenHotelCancelCommentTypeLabels[comment.status]}
                  </TableCell>
                  <TableCell>{comment.content}</TableCell>
                  <TableCell nowrap>{comment.commentedAt}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div style={{ margin: '10px 0' }}>
          <Select value={status} onChange={e => setStatus(e.target.value as RakutenHotelCancelCommentType)}>
            {Object.entries(rakutenHotelCancelCommentTypeLabels).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </div>
        <div style={{ margin: '10px 0' }}>
          <Textarea
            placeholder="コメント内容を入力"
            value={content}
            onChange={e => setContent(e.target.value)}
            rowsMin={5}
            size="medium"
            fullWidth
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button onClick={saveComment} disabled={content.trim() === ''} loading={isLoading}>
          送信
        </Button>
      </ModalFooter>
    </Modal>
  );
});

export default CancelInventoryCommentModal;
